import is from '@sindresorhus/is';
import { motion } from 'framer-motion';
import { type FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { theme } from '../../theme/cobrainer-theme';
import { PANEL_NUDGE_DURATION, REDUCED_MOTION_QUERY, TILE_EXIT_VARIANT } from './constants';
import { TileResizeHandle } from './TileResizeHandle';
import { useSetTileBox, useSetTileSlot, useTileErrorBoundary, useTileWidthContext } from './TilingEngineContext';
import { isZeroWidth } from './tile-motion';
import type { TilingEnginePanelProps } from './types';

const GAP_CANCELLING_MARGIN_LEFT = `calc(-1 * ${theme.spacing.m})`;
const PANEL_ENTER_TRANSLATE = 'translateX(15px)';
const PANEL_ENTER_REST = 'translateX(0px)';
const PANEL_NUDGE_TRANSITION = {
    duration: PANEL_NUDGE_DURATION,
    ease: [0.215, 0.61, 0.355, 1],
} as const;

export const TilingEnginePanel: FunctionComponent<TilingEnginePanelProps> = ({
    children,
    id,
    maxWidth,
    minWidth,
    resizeKey,
    width,
    zIndex,
}) => {
    const setTileSlot = useSetTileSlot();
    const setTileBox = useSetTileBox();
    const tileWidths = useTileWidthContext();
    const ErrorBoundary = useTileErrorBoundary();
    const slotRef = useRef<HTMLDivElement>(null);
    const boxRef = useRef<HTMLDivElement | null>(null);
    const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY) === true;
    const [clipEnter, setClipEnter] = useState(true);
    const widthStyle = tileWidths.get(id);
    const resolvedWidth = widthStyle?.width ?? width ?? '50%';
    const resolvedMinWidth = widthStyle?.minWidth ?? minWidth ?? 0;
    const isWidthClosed = isZeroWidth(width);
    const skipEnter = reducedMotion || isWidthClosed;
    const isResizable = is.nonEmptyString(resizeKey) && !isWidthClosed;
    // Clipping hides the enter nudge. Without a nudge, only a resizable tile stops clipping, so its
    // resize handle in the gap stays visible.
    const isClipped = isWidthClosed || (reducedMotion ? !isResizable : clipEnter);

    const setOuterRef = useCallback(
        (node: HTMLDivElement | null) => {
            boxRef.current = node;
            setTileBox(id, node);
        },
        [id, setTileBox],
    );

    useEffect(() => {
        setTileSlot(id, slotRef.current);

        return () => {
            setTileSlot(id, null);
        };
    }, [id, setTileSlot]);

    const handleEnterComplete = useCallback(() => {
        setClipEnter(false);
    }, []);

    return (
        <motion.div
            ref={setOuterRef}
            data-tile-id={id}
            initial={false}
            exit={TILE_EXIT_VARIANT}
            style={{
                flexShrink: 0,
                height: '100%',
                marginLeft: isWidthClosed ? GAP_CANCELLING_MARGIN_LEFT : 0,
                maxWidth: widthStyle?.maxWidth ?? maxWidth,
                minWidth: isWidthClosed ? 0 : resolvedMinWidth,
                overflow: isClipped ? 'hidden' : 'visible',
                position: zIndex === undefined && !isResizable ? undefined : 'relative',
                transition: 'none',
                width: isWidthClosed ? 0 : resolvedWidth,
                zIndex,
            }}
            transition={{ duration: 0, ease: 'easeOut' }}
            variants={{ [TILE_EXIT_VARIANT]: { opacity: 1 } }}
        >
            {isResizable ? <TileResizeHandle boxRef={boxRef} resizeKey={resizeKey} /> : null}
            <motion.div
                ref={slotRef}
                className="h-full w-full"
                initial={skipEnter ? false : { transform: PANEL_ENTER_TRANSLATE }}
                animate={{ transform: PANEL_ENTER_REST }}
                transition={skipEnter ? { duration: 0 } : PANEL_NUDGE_TRANSITION}
                onAnimationComplete={handleEnterComplete}
            >
                {is.nullOrUndefined(ErrorBoundary) ? children : <ErrorBoundary tileId={id}>{children}</ErrorBoundary>}
            </motion.div>
        </motion.div>
    );
};
